import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffSessionService } from './staff-session.service';
import { NewLoginWithDomainVerificationServices } from '../../new-login-with-domain-verification/new-login-with-domain-verification.service';

// Some admin-server endpoints (generate/token/product for extension
// services, create/broadcast, ...) only accept an admin-issued token — the
// staff session token (from the separate staff server) 401s there. Rather
// than wait on a staff-scoped backend endpoint, this re-authenticates the
// staff member on demand via the old admin-facing staff login (/login/staff)
// and stores the resulting token as usertoken, shared by every admin-server
// call site (HeaderService, BroadcastService, ...) that reads it.
//
// Usage: call requestVerification(label) from any component, then await the
// promise it returns — it resolves true once the modal succeeds (or the
// cached token is reused without prompting), false if the user cancels.
// Bind the *ngIf="showModal" state below into a single shared modal in the
// staff layout, since it's the one component always present in the DOM.
@Injectable({ providedIn: 'root' })
export class StaffAdminVerifyService {
    showModal = false;
    label = '';
    mobileNumber = '';
    password = '';
    showPassword = false;
    loading = false;
    error = '';

    private resolver: ((success: boolean) => void) | null = null;

    constructor(
        private staffSession: StaffSessionService,
        private translate: TranslateService,
        private newLoginWithDomainVerificationServices: NewLoginWithDomainVerificationServices
    ) {}

    // Resolves immediately (true) if an admin token is already cached from a
    // prior verification this session; otherwise opens the modal and
    // resolves once the staff member submits valid credentials or cancels.
    requestVerification(label: string): Promise<boolean> {
        if (localStorage.getItem('usertoken')) {
            return Promise.resolve(true);
        }
        this.label = label;
        this.mobileNumber = '';
        this.password = '';
        this.showPassword = false;
        this.error = '';
        this.showModal = true;
        return new Promise<boolean>(resolve => {
            this.resolver = resolve;
        });
    }

    handleMobileNumberKeyDown(event: KeyboardEvent): void {
        const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab', 'Home', 'End'];
        if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
            event.preventDefault();
        }
    }

    cancel() {
        this.showModal = false;
        this.resolver?.(false);
        this.resolver = null;
    }

    async submit() {
        const mobileNumber = this.mobileNumber.trim();
        if (!mobileNumber || !this.password.trim()) {
            this.error = await firstValueFrom(this.translate.get('emptyMobileNumberOrPassword'));
            return;
        }
        if (!/^\d+$/.test(mobileNumber)) {
            this.error = await firstValueFrom(this.translate.get('onlyNumberAllowed'));
            return;
        }
        this.loading = true;
        this.error = '';
        try {
            const info = this.staffSession.getSchoolAndStaffInfo();
            const staffDetails = this.staffSession.getStaffDetails();
            const domainName = info?.SchoolDomain || staffDetails?.SchoolDomain;
            const response = await firstValueFrom(
                this.newLoginWithDomainVerificationServices.staffLoginApi({
                    userName: mobileNumber,
                    password: this.password,
                    domainName,
                })
            );
            const data = response as any;
            if (data?.token) {
                localStorage.setItem('usertoken', data.token);
                this.showModal = false;
                this.resolver?.(true);
                this.resolver = null;
            } else {
                this.error = data?.message || await firstValueFrom(this.translate.get('invalidStaffCredentials'));
            }
        } catch (error: any) {
            this.error = error?.error?.message || await firstValueFrom(this.translate.get('errorMessage'));
        } finally {
            this.loading = false;
        }
    }
}
